import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, CreditCard, FileText, Loader2 } from 'lucide-react';
import { PaymentModal } from '../components/shop/PaymentModal';
import { UpdatedBadge, SavingsTag, imgSrc, coverFallback } from '../components/shop/ProductCard';
import { SampleModal } from '../components/shop/SampleModal';
import { regionSlug } from '../lib/regions';
import { useSeo, productLd } from '../lib/seo';
import { track } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Siblings = ({ product }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!product.guide_id) return;
    axios.get(`${API}/products`).then((r) => setItems((r.data.products || []).filter((p) => p.guide_id === product.guide_id && p.id !== product.id))).catch(() => {});
  }, [product.guide_id, product.id]);
  if (items.length === 0) return null;
  return (
    <div className="mt-16" data-testid="product-siblings">
      <p className="eyebrow mb-4"><span className="w-10 h-px bg-[#C5A059]" /> Also in {product.guide_title}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <Link key={p.id} to={`/shop/p/${p.id}`} data-testid={`sibling-${p.product_key}`} className="card-hover p-5 flex items-center justify-between gap-4">
            <span className="text-sm text-[#1E1622]">{p.title}</span>
            <span className="font-display text-xl text-[#3B164C] flex-shrink-0">A${p.price}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [sample, setSample] = useState(null);

  useEffect(() => {
    setLoading(true); window.scrollTo(0, 0);
    axios.get(`${API}/products/${id}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false));
  }, [id]);
  useSeo(p && {
    title: `${p.destination ? `Moving to ${p.destination} from Australia: ` : ''}${p.title}`,
    description: p.tagline || p.description, image: p.image_url, type: 'product',
    jsonLd: productLd({ name: `${p.guide_title ? `${p.guide_title}: ` : ''}${p.title}`, description: p.description, image: p.image_url, sku: p.id, offers: [{ name: p.title, price: p.price }] }),
  }, [p]);
  useEffect(() => { if (p) track('view_item', { currency: 'AUD', value: Number(p.price), items: [{ item_id: p.id, item_name: p.title, item_category: p.destination, price: Number(p.price) }] }); }, [p]);

  if (loading) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;
  if (!p) return <div className="min-h-screen pt-32 text-center" data-testid="product-not-found"><p className="text-[#1E1622]/60">This guide isn't available right now.</p><Link to="/shop" className="link-gold mt-4 inline-block">Back to shop</Link></div>;

  const marketing = p.fees?.marketing_lines?.[0];
  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="product-page">
      <div className="container-custom">
        <Link to="/shop" className="link-gold mb-8 inline-flex"><ArrowLeft size={18} /> Back to shop</Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="relative rounded-3xl overflow-hidden bg-[#2D1037] aspect-[4/5] lg:sticky lg:top-28 self-start">
            {p.image_url && <img src={imgSrc(p.image_url)} onError={coverFallback} alt={p.title} className="w-full h-full object-cover" />}
            <UpdatedBadge fees={p.fees} className="absolute top-5 left-5" />
          </div>
          <div>
            {p.guide_title && <Link to={`/destinations/${regionSlug(p.destination)}`} data-testid="product-page-destination" className="eyebrow mb-3 hover:text-[#3B164C]"><span className="w-10 h-px bg-[#C5A059]" /> {p.origin} → {p.destination}</Link>}
            <h1 className="font-display text-4xl sm:text-5xl font-medium text-[#1E1622] mb-2" data-testid="product-title">{p.title}</h1>
            {p.guide_title && <p className="text-[#1E1622]/50 italic mb-4">{p.guide_title}{p.guide_subtitle ? `: ${p.guide_subtitle}` : ''}</p>}
            {p.tagline && <p className="text-lg text-[#3B164C] font-light mb-6">{p.tagline}</p>}
            {p.badge?.badge_url && (
              <a href={p.whats_new_url || p.badge.link_url} target="_blank" rel="noopener noreferrer" data-testid="product-page-whats-new" className="inline-flex items-center gap-3 mb-6 text-sm text-[#3B164C] hover:text-[#C5A059] transition-colors duration-200">
                <img src={p.badge.badge_url} alt={p.badge.label || 'Edition badge'} height="28" className="h-7 w-auto" data-testid="product-page-badge" />
                <span className="underline underline-offset-4">See what's new</span>
              </a>
            )}
            <div className="flex flex-wrap items-center gap-4 mb-2">
              <span className="font-display text-4xl text-[#3B164C]" data-testid="product-page-price">A${p.price}</span>
              <button onClick={() => setBuying(p)} data-testid="product-page-buy" className="btn-gold inline-flex items-center gap-2"><CreditCard size={16} /> Buy now</button>
              {p.sample_url && <button onClick={() => setSample(p)} data-testid="product-page-sample" className="link-gold text-sm"><FileText size={14} /> Read a free sample</button>}
            </div>
            <SavingsTag product={p} className="mb-3" />
            {marketing && <p className="text-sm text-[#1E1622]/50 mb-8" data-testid="product-page-marketing">{marketing}</p>}
            <p className="text-[#1E1622]/70 leading-relaxed mb-8 mt-6" data-testid="product-description">{p.description}</p>
            {p.bullets?.length > 0 && (
              <div className="bg-[#F5EFE9] rounded-2xl p-6" data-testid="product-bullets">
                <p className="font-display text-xl text-[#1E1622] mb-4">What's inside</p>
                <ul className="space-y-3">
                  {p.bullets.map((b, i) => <li key={i} className="flex gap-3 text-sm text-[#1E1622]/75"><Check size={16} className="text-[#C5A059] flex-shrink-0 mt-0.5" /> {b}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
        <Siblings product={p} />
      </div>
      <PaymentModal product={buying} onClose={() => setBuying(null)} />
      <SampleModal product={sample} onClose={() => setSample(null)} />
    </div>
  );
}
